import { Analytics } from '@cobliteam/analytics';
import { type Driver, isWebDataError, isWebDataSuccess, type VehicleRoute, type VehicleRouteFilters, type WebData } from '@cobliteam/api-lib';
import { type DateRange, Location, type PageableContent, type Sort, type SortOrder, type UUID, type Vehicle } from '@cobliteam/common-types';
import { useGlobalFilterStore } from '@cobliteam/hooks';
import { AiRecommendationJourney } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import { useTranslation } from '@cobliteam/i18n';
import { Button, DatePicker, Divider, Loading } from '@cobliteam/ui/components';
import { AiRecommendationBanner } from '@cobliteam/ui-business';
import Blankslate, { BlankslateIcon, BlankslateInfo } from '@cobliteam/ui-business/components/Blankslate';
import Page from '@cobliteam/ui-business/components/layout/Page';
import moment from 'moment';
import queryString from 'query-string';
import React from 'react';
import { FiltersSection } from './components/FiltersSection/FiltersSection';
import { RoutesTable } from './components/RoutesTable/RoutesTable';

export interface RoutesHistoryProps {
  vehicleRoutesPageWebData: WebData<PageableContent<VehicleRoute>>;

  vehiclesWebData: WebData<Vehicle[]>;
  driversById: Map<UUID, Driver>;
  vehiclesById: Map<UUID, Vehicle>;

  loadOnChanges: (period: DateRange, page: number, size: number, filters?: VehicleRouteFilters, sort?: Sort) => void;
  loadPageContent: () => void;
  onRequestAddress: (latitude: number, longitude: number) => void;
}

const DEFAULT_PERIOD: DateRange = {
  start: moment().startOf('day').subtract(1, 'day').valueOf(),
  end: moment().endOf('day').subtract(1, 'day').valueOf(),
};

export const RoutesHistory = ({ vehicleRoutesPageWebData, driversById, vehiclesWebData, vehiclesById, loadOnChanges, loadPageContent, onRequestAddress }: RoutesHistoryProps) => {
  const { t } = useTranslation('tracking');
  const vehicleGroupIds = useGlobalFilterStore((state) => state.vehicleGroupIds);

  const [period, setPeriod] = React.useState(DEFAULT_PERIOD);
  const [wantedVehicleIds, setWantedVehicleIds] = React.useState<UUID[]>([]);
  const [wantedTimeSelection, setWantedTimeSelection] = React.useState<DateRange>({
    start: 1,
    end: 24 * 60 * 60 * 1000,
  });
  const [sort, setSort] = React.useState<Sort | undefined>(undefined);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  React.useEffect(() => {
    Analytics.track('Tracking History Page Viewed');
    loadPageContent();
  }, []);

  const condenseFilters = (): VehicleRouteFilters => {
    const wantedGroupIds = vehicleGroupIds.length ? vehicleGroupIds : undefined;
    const vehicleIds = wantedVehicleIds.length ? wantedVehicleIds : undefined;

    return { wantedGroups: wantedGroupIds, wantedVehicleIds: vehicleIds };
  };

  React.useEffect(() => {
    setCurrentPage(0);
    loadOnChanges(period, 0, rowsPerPage, condenseFilters(), sort);
  }, [period, rowsPerPage, vehicleGroupIds, wantedVehicleIds, sort]);

  React.useEffect(() => {
    setWantedVehicleIds([]);
  }, [vehicleGroupIds]);

  const handleReloadOnError = () => {
    Analytics.track('Tracking History Reloaded On Error');
    loadPageContent();
    loadOnChanges(period, currentPage, rowsPerPage, condenseFilters(), sort);
  };

  const handleDayChange = (beginDayInMillis: number) => {
    const newPeriodEndMoment = moment(beginDayInMillis).endOf('day');
    const newPeriod = {
      start: beginDayInMillis,
      end: newPeriodEndMoment.valueOf(),
    };
    Analytics.track('Tracking History Day Selection Changed');
    setPeriod(newPeriod);
  };

  const handleVehicleFilterApply = (newVehicleIds: UUID[]) => {
    Analytics.track('Tracking History Filtered By Vehicles', {
      numberOfVehicles: newVehicleIds.length,
    });
    setWantedVehicleIds(newVehicleIds);
  };

  const handleTimeFilterApply = (timeSelection: DateRange) => {
    const hoursRange = moment(timeSelection.end).diff(moment(timeSelection.start), 'hours');
    Analytics.track('Tracking History Filtered By Hour', { hoursRange });
    setWantedTimeSelection(timeSelection);
  };

  const handlePaginationChange = (newPage: number, newRowsPerPage: number) => {
    Analytics.track('Tracking History Pagination Changed', {
      newPage,
      newRowsPerPage,
    });
    setCurrentPage(newPage);
    setRowsPerPage(newRowsPerPage);
    loadOnChanges(period, newPage, newRowsPerPage, condenseFilters(), sort);
  };

  const handleRequestSort = (column: string, order: SortOrder) => {
    Analytics.track('Tracking History Table Sorted', { column, order });
    setSort({ column, order });
  };

  const handleClickHook = (vehicleId: UUID) => {
    Analytics.track('See Vehicle Tracking On Map From Tracking History Clicked');

    const params = queryString.stringify({
      vehicle_id: vehicleId,
      date: period.start,
    });
    const url = `${window.location.origin}${Location.TRACKING}?${params}`;
    window.open(url, '_blank')!.focus();
  };

  const toTableRow = (vehicleRoute: VehicleRoute, vehicle?: Vehicle) => {
    const { id } = vehicleRoute.vehicle;

    return {
      id,
      vehicleRoute,
      vehicleId: vehicle?.id || '',
      cobliId: vehicle?.cobliId,
      vehicle,
    };
  };

  const getTableRows = (vehicleRoutes: VehicleRoute[], vehicles: Vehicle[]) => {
    return vehicleRoutes.map((vehicleRoute) => {
      const currentVehicle = vehicles.find((vehicle) => vehicle.id === vehicleRoute.vehicle.id);

      return toTableRow(vehicleRoute, currentVehicle);
    });
  };

  const renderRoutesTimeline = () => {
    if (isWebDataError(vehicleRoutesPageWebData)) {
      return (
        <Blankslate>
          <BlankslateIcon iconName='message-square-x' />
          <BlankslateInfo title='Ops! Ocorreu um erro no sistema.' />
          <Button variant='ghost' onClick={handleReloadOnError}>
            {t('reload')}
          </Button>
        </Blankslate>
      );
    }
    if (isWebDataSuccess(vehicleRoutesPageWebData) && isWebDataSuccess(vehiclesWebData)) {
      const vehicleRoutesPage = vehicleRoutesPageWebData.data;
      const vehicles = vehiclesWebData.data;

      return (
        <>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-2'>
              <DatePicker
                mode='single'
                selected={new Date(period.start)}
                disabledDays={{ after: new Date() }}
                onSelect={(date) => handleDayChange(date?.valueOf() ?? 0)}
                className='w-fit'
              />
              <FiltersSection
                period={period}
                vehicles={Array.from(vehiclesById.values())}
                wantedVehicleIds={wantedVehicleIds}
                wantedTimeSelection={wantedTimeSelection}
                onVehicleFilterChanged={handleVehicleFilterApply}
                onTimeFilterChanged={handleTimeFilterApply}
              />
            </div>
            <Divider />
          </div>

          {vehicleRoutesPage.empty ? (
            <Blankslate>
              <BlankslateIcon iconName='message-square-x' />
              <BlankslateInfo title={t('no_routes_for_filters')} />
            </Blankslate>
          ) : (
            <RoutesTable
              period={period}
              wantedTimeSelection={wantedTimeSelection}
              rows={getTableRows(vehicleRoutesPage.content, vehicles)}
              totalRowsLength={vehicleRoutesPage.totalElements}
              rowsPerPage={vehicleRoutesPage.size}
              currentPage={vehicleRoutesPage.number}
              sort={sort}
              driversById={driversById}
              onSeeOnMapClick={handleClickHook}
              onPaginationChange={handlePaginationChange}
              onRequestSort={handleRequestSort}
              onRequestAddress={onRequestAddress}
            />
          )}
        </>
      );
    }
    return <Loading />;
  };

  return (
    <Page>
      <Page.Header>
        <Page.Title />
      </Page.Header>
      <Page.Body>
        <AiRecommendationBanner journeyId={AiRecommendationJourney.SETUP_DRIVER_ASSOCIATION} />
        {renderRoutesTimeline()}
      </Page.Body>
    </Page>
  );
};
